/**
 * One list from two stores.
 *
 * Designs live in two places — this device and the account — and the list has
 * to show both without lying about either. The rules are small and the failure
 * modes are not: showing a project twice makes people delete the "duplicate",
 * and showing somebody else's local work to a signed-in stranger on a shared
 * machine is worse than that.
 *
 * Pure, because those rules are where the damage is. `shared/migration.ts` has
 * the same shape and the same reasoning.
 */
import type { GarmentType } from "../../shared/format";
import type { LocalProjectMeta } from "../../shared/migration";
import { claimableFor } from "../../shared/migration";
import { remainingProjectQuota, type Tier } from "../../shared/tiers";
import type { CloudProject } from "../api/projects";

export type ProjectEntry =
  | {
      kind: "cloud";
      id: string;
      title: string;
      updatedAt: number;
      /** Past the plan's quota: openable and exportable, not saveable. */
      readOnly: boolean;
      /** Shirt or pants. Served by the API and, until now, never read. */
      garment?: GarmentType;
      /**
       * A picture of the composite, from the copy on this device.
       *
       * A design that lives only in the account has none until it is opened —
       * which is the moment one gets made. Fetching a 200 KB archive to render
       * a 12 KB picture would undo the reason the stores are separate.
       */
      thumb?: Uint8Array;
      /**
       * The copy on this device, when there is one.
       *
       * A design made here and uploaded appears as its cloud row — the local
       * twin is hidden so the same design is not listed twice. But the editor
       * opens local archives, so without this the row a user is most likely to
       * click is the one row that cannot say where its own bytes are. Absent
       * means the design lives only in the account and has to be fetched.
       */
      localId?: string;
    }
  | {
      kind: "local";
      id: string;
      title: string;
      updatedAt: number;
      sizeBytes: number;
      /** Offerable to the signed-in user. False when someone else claimed it. */
      claimable: boolean;
      /** Shirt or pants. Absent on rows whose archive could not be read. */
      garment?: GarmentType;
      /** A picture of the composite, when this device has one. */
      thumb?: Uint8Array;
      /**
       * Was in the account, and is not any more.
       *
       * The account copy was deleted — here, or on another machine — leaving
       * this archive behind. It is shown rather than hidden, because hidden it
       * is unreachable forever, but it has to be *labelled*: without this it
       * renders as an ordinary design that appeared out of nowhere, which is
       * exactly what it looks like to somebody who never deleted anything on
       * this device.
       */
      stranded?: boolean;
    };

/**
 * Merge the two stores into what the page renders.
 *
 * A local project that has already been migrated is **not** shown: it is the
 * same design as the cloud row beside it, and two rows for one design is how
 * somebody deletes the copy they meant to keep.
 *
 * When signed out, every unclaimed local project is shown — there is no account
 * to attribute them to, and hiding a person's own work behind a sign-in wall on
 * their own machine would be absurd.
 */
export function mergeProjects(
  cloud: readonly CloudProject[],
  local: readonly LocalProjectMeta[],
  userId: string | null,
): ProjectEntry[] {
  // cloud project id → the local archive it was uploaded from, if that archive
  // is still on this device. Built once rather than scanned per row.
  const twins = new Map<string, LocalProjectMeta>();
  for (const p of local) {
    if (p.migratedTo) twins.set(p.migratedTo.projectId, p);
  }

  const entries: ProjectEntry[] = cloud.map((p) => ({
    kind: "cloud",
    id: p.id,
    title: p.title,
    updatedAt: p.updated_at,
    readOnly: p.readOnly,
    garment: p.garment_type,
    // The picture comes from the copy on this device. A cloud-only row has no
    // thumbnail until it is opened, which is the point at which one is made.
    thumb: twins.get(p.id)?.thumb,
    ...(twins.has(p.id) ? { localId: twins.get(p.id)?.id } : {}),
  }));

  const claimable = userId ? new Set(claimableFor(local, userId).map((p) => p.id)) : null;

  const cloudIds = new Set(cloud.map((p) => p.id));

  for (const p of local) {
    let stranded = false;
    if (p.migratedTo) {
      /*
       * Hidden only while the cloud row it points at is actually there.
       *
       * This used to skip every migrated local unconditionally, which is right
       * for the normal case — the cloud row above is this same design, and two
       * rows for one design is how somebody deletes the copy they meant to
       * keep. But when the cloud project goes away, the local archive it was
       * hiding behind goes with it: still on the disk, still consuming the
       * device's storage, and listed nowhere, so no control anywhere can reach
       * it. Showing it again is the only way it is ever recoverable.
       *
       * Signed out, it stays hidden regardless. The design belongs to an
       * account, and offering it to whoever is at a shared machine next is
       * worse than hiding a file its owner can see by signing in.
       */
      const owner = p.migratedTo.userId;
      const orphaned = userId !== null && owner === userId && !cloudIds.has(p.migratedTo.projectId);
      if (!orphaned) continue;
      stranded = true;
    } else if (userId && !claimable?.has(p.id)) {
      // Claimed by somebody else on this shared device.
      continue;
    }

    entries.push({
      kind: "local",
      id: p.id,
      title: p.title,
      updatedAt: p.updatedAt,
      sizeBytes: p.sizeBytes,
      claimable: userId !== null,
      garment: p.garment,
      thumb: p.thumb,
      ...(stranded ? { stranded: true } : {}),
    });
  }

  // Most recently edited first: what someone is most likely to want, and least
  // able to reconstruct.
  return entries.sort((a, b) => b.updatedAt - a.updatedAt);
}

export interface QuotaLine {
  text: string;
  /** At the ceiling. The earned upgrade moment, not a wall. */
  full: boolean;
}

/**
 * What the account is using, against what it allows.
 *
 * Always a fraction, never a bare count — "2 designs" says nothing, and the
 * denominator is the entire reason the number is interesting.
 */
export function quotaLine(tier: Tier, cloudCount: number): QuotaLine {
  const remaining = remainingProjectQuota(tier, cloudCount);
  const limit = cloudCount + remaining;
  return {
    text: `${cloudCount} of ${limit} designs in your account`,
    full: remaining === 0,
  };
}

/** How many local designs could still be moved up, given the quota. */
export function claimableCount(local: readonly LocalProjectMeta[], userId: string | null): number {
  if (!userId) return 0;
  return claimableFor(local, userId).length;
}
